/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  signal
} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import {Subscription} from 'rxjs';

import template from './co2-stats.component.html';
import {StatsApiService} from './stats-api.service';
import {OBJECT_TYPE} from '../constants/object-type';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {deferSignalStateWrite} from '../util/template-state';
import {APP_SHARED} from '../app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, TranslateModule],
  selector: 'adn-co2-stats-component',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Co2StatsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: any;
  @Input() objectType: string;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  readonly engaged = true;

  readonly allStats = signal<any>({});
  readonly haveStats = signal(false);
  readonly error = signal(false);
  readonly isPending = signal(false);
  readonly noData = signal<string | null>(null);
  readonly rImpsTotals = signal(0);
  readonly trans = signal('');

  private statsSubscription?: Subscription;
  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: Highcharts.Options | null = null;
  private lastRequestKey = '';
  private requestVersion = 0;
  private destroyed = false;

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly adnLog: AdnLogService
  ) {
  }

  get resolvedObjectType(): string {
    return this.objectType;
  }

  ngOnInit(): void {
    this.ensureStatsLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model || changes.objectType) {
      this.ensureStatsLoaded();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requestVersion++;
    this.statsSubscription?.unsubscribe();
    this.destroyChart();
  }

  private ensureStatsLoaded(): void {
    if (!this.model?.id) {
      return;
    }

    const requestKey = `${this.resolvedObjectType || 'Creative'}:${this.model.id}`;
    if (requestKey === this.lastRequestKey) {
      return;
    }

    this.lastRequestKey = requestKey;
    this.loadStats();
  }

  private loadStats(): void {
    const objectTypeEl = OBJECT_TYPE[this.resolvedObjectType] || OBJECT_TYPE.Creative;
    const co2Params: Record<string, any> = {
      groupBy: 'MONTHLY'
    };

    co2Params[objectTypeEl.apiParam + 'Id'] = this.model.id;

    const requestVersion = ++this.requestVersion;
    this.trans.set(objectTypeEl.name.toLowerCase());
    this.error.set(false);
    this.haveStats.set(false);
    this.isPending.set(true);
    this.noData.set(null);
    this.rImpsTotals.set(0);
    this.chartOptions = null;
    this.destroyChart();
    this.allStats.set({
      programmaticIndustry: {
        name: 'Industry Average (Programmatic)',
        ratio: 4.24
      },
      directIndustry: {
        name: 'Industry Average (Direct)',
        ratio: 0.66
      },
      adn: {}
    });

    this.statsSubscription?.unsubscribe();
    this.statsSubscription = this.statsApi.getCo2(co2Params).subscribe({next: (stats) => {
      deferSignalStateWrite(() => {
        if (this.destroyed || requestVersion !== this.requestVersion) {
          return;
        }
        const rendered = Number(stats?.totals?.rendered) || 0;
        const totalEmissions = Number(stats?.totals?.totalEmissions) || 0;
        const emissions = Math.trunc(totalEmissions * 1000);
        const ratio = rendered > 0 ? emissions / rendered : 0;
        const series: Highcharts.SeriesOptionsType[] = [];

        const nextAllStats = {...this.allStats()};

        Object.keys(nextAllStats).forEach((key) => {
          if (key === 'adn') {
            return;
          }
          const benchmark = {...nextAllStats[key]};
          const benchmarkEmission = Math.trunc(benchmark.ratio * rendered);
          benchmark.emissions = benchmarkEmission;
          benchmark.adnRatio = benchmarkEmission > 0 ? emissions / benchmarkEmission : 0;
          nextAllStats[key] = benchmark;
          series.push({
            type: 'bar',
            name: benchmark.name,
            data: [benchmark.emissions]
          });
        });

        nextAllStats.adn = {
          emissions,
          emissionsRaw: emissions,
          ratio
        };

        series.push({
          type: 'bar',
          name: 'This ' + objectTypeEl.name,
          data: [emissions]
        });

        this.chartOptions = this.createChartOptions(series);
        this.allStats.set(nextAllStats);
        this.rImpsTotals.set(rendered);
        this.isPending.set(false);
        this.haveStats.set(true);
        this.renderChart();
      });
    }, error: (data) => {
      deferSignalStateWrite(() => {
        if (this.destroyed || requestVersion !== this.requestVersion) {
          return;
        }
        this.adnLog.warn(data);
        this.isPending.set(false);
        if (data.status !== -1) {
          this.error.set(true);
        }
      });
    }});
  }

  private createChartOptions(series: Highcharts.SeriesOptionsType[]): Highcharts.Options {
    return {
      chart: {
        type: 'bar',
        height: 200
      },
      yAxis: {
        title: {
          text: 'Emissions in grammes'
        }
      },
      xAxis: {
        categories: [''],
        title: {
          text: null
        }
      },
      tooltip: {
        shared: true,
        valueSuffix: 'g'
      },
      series,
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      exporting: {
        enabled: true
      }
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || this.rImpsTotals() <= 0) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
